<script setup lang='ts'>
import { APP_ROUTER, imageTransform } from '@qctsw/common'
import { isArray } from '@qctsw/utils'
import SeriesSummary from './components/SeriesSummary.vue'
import Advantages from './components/Advantages.vue'
import Statistics from './components/Statistics.vue'
import NewComplaint from './components/NewComplaint.vue'

import { ModelYear, type SeriesDetailLabel, logicReputationDetail, logicReputationStatistics } from '@/logic/reputation/detail'
import type { CarSelected } from '~/components/Basic/CarSelectFilter/hooks'
import { ISSUE_TYPE_CODE_ENUM } from '~/shared/constants'
import { useGetRouteParam } from '~/shared/function'

const router = useRouter()
const seriesId = useGetRouteParam('seriesId', '')
const {
  state,
  detailData,
  getDetail,
} = logicReputationDetail()

// statistics
const {
  complaintIssueStatistics,
  getIssueStat,
  initHandle: statisticsInitHandle,
} = logicReputationStatistics()

// 优点
const seriesMerits = computed(() => {
  return detailData.value?.seriesMerits?.split(/ |\s/)?.filter(v => !!v) || []
})
// 缺点
const seriesDefects = computed(() => {
  return detailData.value?.seriesDefects?.split(/ |\s/)?.filter(v => !!v) || []
})
// 车型年份
const modelYears = computed(() => {
  const list: string[] = [ModelYear.all]
  detailData.value?.modelYearLs?.forEach((v) => {
    list.push(`${v}`)
  })
  return list
})

const labels = computed(() => {
  const labels: SeriesDetailLabel = {
    quality: [],
    sale: [],
    afterSale: [],
  }
  if (!complaintIssueStatistics.value.source.length || !isArray(complaintIssueStatistics.value.source))
    return labels
  const data = complaintIssueStatistics.value.source.sort((a, b) => b.complainTotal - a.complainTotal)
  for (const item of data) {
    switch (item.issueTypeCode) {
      case ISSUE_TYPE_CODE_ENUM.quality:
        labels.quality.push({
          content: item.issueLabel,
          tips: `${item.complainTotal}个`,
        })
        break
      case ISSUE_TYPE_CODE_ENUM.sale:
        labels.sale.push({
          content: item.issueLabel,
          tips: `${item.complainTotal}个`,
        })
        break
      case ISSUE_TYPE_CODE_ENUM.afterSale:
        labels.afterSale.push({
          content: item.issueLabel,
          tips: `${item.complainTotal}个`,
        })
        break
    }
  }
  return labels
})

function carSelected(data: CarSelected) {
  if (!data.series?.id)
    return
  router.push(APP_ROUTER.reputationDetail(data.series?.id))
}

async function initHandler() {
  statisticsInitHandle()
  await Promise.all([
    getDetail(seriesId.value),
    getIssueStat(),
  ])
}
await initHandler()
</script>

<template>
  <div class="public-container public-page">
    <UtilLoadState :state="state" :reload-handler="initHandler">
      <template v-if="detailData">
        <div class="flex items-center justify-between mt-4 reset">
          <div class="flex items-center">
            <img
              v-if="detailData.carBrandLogo" class="mr-4 w-5 h-5 rounded-full overflow-hidden"
              :src="imageTransform(detailData.carBrandLogo)"
            >
            <h3>
              {{ `${detailData.carBrandName} - ${detailData.carSeriesName}` }}
            </h3>
          </div>
          <ClientOnly>
            <BasicCarSelectFilter
              :brand-id="detailData.carBrandId"
              :series-id="detailData.carSeriesId"
              :show-footer="true"
              :select-level="2"
              @change="carSelected"
            />
          </ClientOnly>
        </div>
        <div class="mt-4 border-1 border-slate-2 rounded-md overflow-hidden">
          <div class="py-sm px-6 bg-blue-5 text-white flex items-center justify-between">
            <div class="text-4.5 font-bold">
              综述
            </div>
            <div class="text-3.5">
              每日凌晨五点更新
            </div>
          </div>
          <div class="flex text-3.5">
            <SeriesSummary
              :brand-id="detailData.carBrandId"
              :series-id="detailData.carSeriesId"
              :series-name="detailData.carSeriesName"
              :series-cover-img="detailData.seriesCoverImg"
              :series-bodys="`${detailData.seriesBodys || ''}${detailData.seriesGrade && detailData.seriesBodys ? ` - ` : ''} ${detailData.seriesGrade || ''}`"
              :series-displacements="detailData.seriesDisplacements"
              :series-gearboxs="detailData.seriesGearboxs"
              :stat-qt="detailData.statQt"
              :stat-finish-rate="detailData.statFinishRate"
              :stat-complain-total="detailData.statComplainTotal"
              :stat-cacsi="detailData.statCacsi"
              :labels="labels"
            />
            <!-- <div class="p-6 w-95 flex-shrink-0"> -->
            <!-- <Advantages :advantages="seriesMerits" :disadvantages="seriesDefects" /> -->
            <!-- </div> -->
          </div>
        </div>
        <div class="mt-8">
          <BasicTitle title="车型故障统计" />
          <div class="flex mt-6">
            <div class="flex-grow min-w-0 overflow-hidden mr-sm">
              <Statistics :model-years="modelYears" />
              <NewComplaint class="mt-6" :brand-name="detailData.carBrandName" :series-name="detailData.carSeriesName" :brand-id="detailData.carBrandId" :series-id="detailData.carSeriesId" />
            </div>
            <div class="w-85 flex-shrink-0">
              <RankHotSeries />
              <RankHotNews class="mt-6 sticky top-25" />
            </div>
          </div>
        </div>
      </template>
      <BasicEmpty v-else />
    </UtilLoadState>
  </div>
</template>

<style lang='scss' scoped>

</style>
